import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'antd';
import sum from 'lodash/sum';
import style from './index.scss';
import TRMDrawer, { scenarioTabsConfig } from '../../typologyInstanceDrawer';
import { convertISOToDateFormat } from '../../../../util/timezone';
import { CustomHeader } from '../../components/CustomHeaderComponent/customHeader';
import {
  AlertsLevelComponent,
  ConfigureAndRerunComponent,
  NotesComponent,
  TextComponent,
  TitleComponent
} from '../../components/TableComponents';
import { getStatusForScenario } from '../../trmUtil';
import { TRM_CONSTANTS } from '../../../constants/trmConstants';
import {
  ITEM_OPERATIONS,
  TM_FRAUD_PREVENTION_SOURCE_MAPPING,
  TM_FRAUD_SOURCE_IDS,
  trmResources
} from '../../../../util/GlobalConstant';
import { hasResourceOperation } from '../../../../util';

const SummaryTable = ({
  isLoading,
  cloneScenario,
  scenarios,
  totalScenarios = 0,
  paginationProps,
  onPaginationChange,
  onScenarioSelect,
  authentication,
  from,
  hasAccessToDeployToProduction,
  typlogyActions
}) => {
  const drawerRef = useRef();

  /**
   * onDrawerOpen
   * @param defaultTab
   * @param record
   */
  let RUN_INSTANCE_ACTIONS_ACCESS = hasResourceOperation(
    { data: authentication },
    trmResources.APP_3_RESOURCE_UI_RUN_INSTANCE,
    ITEM_OPERATIONS.READ
  );

  const preventionAlertColors = (key) => {
    let scoreKey = key.toLowerCase();
    let colorCodeLevels = Object.values(typlogyActions);

    for (let level of colorCodeLevels) {
      if (level.displayName.toLowerCase() === scoreKey) {
        if (level.alertLevel === 'High') return '#E94E43';
        if (level.alertLevel === 'Good') return '#EB922A';
        if (level.alertLevel === 'Low') return '#49C93D';
      }
    }
    return 'black';
  };

  const onDrawerOpen = (defaultTab = 0, record) => {
    drawerRef.current.showDrawer(defaultTab, record);
  };

  const columns = RUN_INSTANCE_ACTIONS_ACCESS
    ? [
        {
          title: <CustomHeader title="Run Instance ID" />,
          className: style.bgWhite,
          key: 'scenario',
          width: '8%',
          render: (scenario) => {
            return (
              <TextComponent style={{ color: '#0C8ED7' }} id={scenario.id} />
            );
          }
        },
        {
          title: <CustomHeader title="Run Instance Name" />,
          width: '13%',
          className: style.bgWhite,
          key: 'scenario',
          onCell: (record) => {
            return {
              onClick: (event) => {
                if (event.target instanceof HTMLImageElement) {
                  onDrawerOpen(2, record);
                } else {
                  onDrawerOpen(0, record);
                }
              }
            };
          },
          render: (scenario) => <TitleComponent title={scenario.name} />
        },
        {
          title: <CustomHeader title="Status" />,
          width: '8%',
          className: style.bgWhite,
          key: 'scenario',
          render: (scenario) => {
            const { latestInstance = {} } = scenario;
            const { IN_QUEUE, COMPLETED } = TRM_CONSTANTS;
            const { status = IN_QUEUE } = latestInstance;
            const textStatus = getStatusForScenario(status);
            return (
              <TextComponent
                style={{
                  color: textStatus === COMPLETED ? '#48C93D' : '#3E7AD3'
                }}
                id={textStatus}
              />
            );
          }
        },
        {
          title: <CustomHeader title="Alerts" />,
          width: '5%',
          className: style.bgWhite,
          key: 'scenario',
          render: (scenario) => {
            const { latestInstance = {} } = scenario;
            const { totalAlert = 0 } = latestInstance;
            return <TextComponent id={totalAlert} />;
          }
        },
        {
          title: <CustomHeader title="Alert Level/Actions Count" />,
          width: '14%',
          className: style.bgWhite,
          key: 'scenario',
          render: (scenario) => {
            if (from === 'Prevention') {
              const { latestInstance = {} } = scenario;
              if (latestInstance?.levelsCount && typlogyActions) {
                const keys = Object.keys(latestInstance?.levelsCount);
                return (
                  <div className={style.alertsLevelComponent}>
                    {keys?.map((key) => (
                      <div
                        style={{
                          color: preventionAlertColors(key)
                        }}
                        key={key}>
                        {key}: {latestInstance.levelsCount[key]}
                      </div>
                    ))}
                  </div>
                );
              }
              return (
                <div className={style.alertsLevelComponent}>
                  No data available
                </div>
              );
            } else {
              const { latestInstance = {} } = scenario;
              const {
                lowAlert = 0,
                goodAlert = 0,
                highAlert = 0
              } = latestInstance;

              return (
                <AlertsLevelComponent
                  highCount={highAlert}
                  goodCount={goodAlert}
                  lowCount={lowAlert}
                />
              );
            }
          }
        },
        {
          title: <CustomHeader title="Creator" />,
          className: style.bgWhite,
          key: 'scenario',
          width: '9%',
          render: (scenario) => <TextComponent id={scenario.createdBy} />
        },
        {
          title: <CustomHeader title="Creation Date" />,
          className: style.bgWhite,
          key: 'scenario',
          width: '3%',
          render: (scenario) => (
            <TextComponent id={convertISOToDateFormat(scenario.createdAt)} />
          )
        },

        {
          title: <CustomHeader title="Last Updated" />,
          className: style.bgWhite,
          key: 'scenario',
          width: '9%',
          render: (scenario) => (
            <TextComponent id={convertISOToDateFormat(scenario.lastUpdated)} />
          )
        },
        {
          title: <CustomHeader title="FinCrime Type" />,
          className: style.bgWhite,
          key: 'scenario',
          width: '5%',
          render: (scenario) => (
            <TextComponent
              id={
                scenario.sourceId === TM_FRAUD_SOURCE_IDS.TM
                  ? TM_FRAUD_PREVENTION_SOURCE_MAPPING.TM_AML
                  : scenario.sourceId ===
                    TM_FRAUD_SOURCE_IDS.FRAUD_PREVENTION_ID
                  ? TM_FRAUD_PREVENTION_SOURCE_MAPPING.FRAUD_PREVENTION
                  : TM_FRAUD_PREVENTION_SOURCE_MAPPING.FRAUD_DETECTION
              }
            />
          )
        },
        {
          title: <CustomHeader title="Action" style={style.lastChildHeader} />,
          className: style.bgWhite,
          key: 'scenario',
          width: '12%',
          render: (scenario) => (
            <ConfigureAndRerunComponent
              dispatchCloneScenario={cloneScenario}
              scenario={scenario}
              authentication={authentication}
            />
          )
        }
      ]
    : [
        {
          title: <CustomHeader title="ID" />,
          className: style.bgWhite,
          key: 'scenario',
          width: '5%',
          render: (scenario) => {
            return (
              <TextComponent style={{ color: '#0C8ED7' }} id={scenario.id} />
            );
          }
        },
        {
          title: <CustomHeader title="Name" />,
          width: '25%',
          className: style.bgWhite,
          key: 'scenario',
          onCell: (record) => {
            return {
              onClick: (event) => {
                if (event.target instanceof HTMLImageElement) {
                  onDrawerOpen(2, record);
                } else {
                  onDrawerOpen(0, record);
                }
              }
            };
          },
          render: (scenario) => <TitleComponent title={scenario.name} />
        },
        {
          title: <CustomHeader title="Status" />,
          width: '8%',
          className: style.bgWhite,
          key: 'scenario',
          render: (scenario) => {
            const { latestInstance = {} } = scenario;
            const { IN_QUEUE, COMPLETED } = TRM_CONSTANTS;
            const { status = IN_QUEUE } = latestInstance;
            const textStatus = getStatusForScenario(status);
            return (
              <TextComponent
                style={{
                  color: textStatus === COMPLETED ? '#48C93D' : '#3E7AD3'
                }}
                id={textStatus}
              />
            );
          }
        },
        {
          title: <CustomHeader title="Alerts" />,
          width: '5%',
          className: style.bgWhite,
          key: 'scenario',
          render: (scenario) => {
            const { latestInstance = {} } = scenario;
            const { totalAlert = 0 } = latestInstance;
            return <TextComponent id={totalAlert} />;
          }
        },
        {
          title: <CustomHeader title="Alert Level/Actions Count" />,
          width: '14%',
          className: style.bgWhite,
          key: 'scenario',
          render: (scenario) => {
            const { latestInstance = {} } = scenario;
            const {
              lowAlert = 0,
              goodAlert = 0,
              highAlert = 0
            } = latestInstance;

            return (
              <AlertsLevelComponent
                highCount={highAlert}
                goodCount={goodAlert}
                lowCount={lowAlert}
              />
            );
          }
        },

        {
          title: <CustomHeader title="Creator" />,
          className: style.bgWhite,
          key: 'scenario',
          width: '9%',
          render: (scenario) => <TextComponent id={scenario.createdBy} />
        },
        {
          title: <CustomHeader title="Creation Date" />,
          className: style.bgWhite,
          key: 'scenario',
          width: '9%',
          render: (scenario) => (
            <TextComponent id={convertISOToDateFormat(scenario.createdAt)} />
          )
        },

        {
          title: <CustomHeader title="Last Updated" />,
          className: style.bgWhite,
          key: 'scenario',
          width: '9%',
          render: (scenario) => (
            <TextComponent id={convertISOToDateFormat(scenario.lastUpdated)} />
          )
        },
        {
          title: <CustomHeader title="Source System" />,
          className: style.bgWhite,
          key: 'scenario',
          width: '5%',
          render: (scenario) => (
            <TextComponent
              id={scenario.sourceId === TM_FRAUD_SOURCE_IDS.TM ? 'TM' : 'Fraud'}
              style={{ fontWeight: 'bold' }}
            />
          )
        }
      ];

  const getRowKeyFromRecord = (obj) => obj.id;

  const rowSelection = {
    type: 'radio',
    onChange: (selectedRowKeys) => {
      onScenarioSelect?.(selectedRowKeys);
    },
    getCheckboxProps: (record) => {
      const { latestInstance = {} } = record;
      const { IN_QUEUE, COMPLETED } = TRM_CONSTANTS;
      const { status = IN_QUEUE } = latestInstance;
      return {
        disabled: status !== COMPLETED
      };
    }
  };

  return (
    <>
      <TRMDrawer tabsConfig={scenarioTabsConfig} ref={drawerRef} />
      <Table
        loading={isLoading}
        columns={columns}
        rowSelection={hasAccessToDeployToProduction ? rowSelection : null}
        rowKey={getRowKeyFromRecord}
        rowClassName={style.tableRowClass}
        dataSource={scenarios}
        pagination={{
          total: totalScenarios,
          onChange: onPaginationChange,
          ...paginationProps
        }}
      />
    </>
  );
};

SummaryTable.propTypes = {
  scenarios: PropTypes.array,
  cloneScenario: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  totalScenarios: PropTypes.number,
  paginationProps: PropTypes.object,
  onPaginationChange: PropTypes.func,
  onScenarioSelect: PropTypes.func
};

export default SummaryTable;
